import {
  deserializeYesplanAPIDateAndTime,
  withYesplanDayStartEndDo
} from './dateTimeUtilities';
import DayRosters from '../roster/RosterFormatComponents/DayRosters';
import RosterBooking from '../roster/RosterFormatComponents/RosterBooking';
import RosterShift from '../roster/RosterFormatComponents/RosterShift';
import RosterUnavailability from '../roster/RosterFormatComponents/RosterUnavailability';
import RosterBreak from '../roster/RosterFormatComponents/RosterBreak';

function isBreak(roster) {
  return roster.scheduletype === 'break';
}

function isShift(roster) {
  return roster.scheduletype === 'shift';
}

function isResourceBooking(roster) {
  return roster.scheduletype === 'resourcebooking';
}

function isUnavailability(roster) {
  return roster.scheduletype === 'unavailability';
}

function withStartEndAndHasActualsDo(schedule, functionToApply) {
  const startString = schedule.actualstart || schedule.start;
  const endString = schedule.actualend || schedule.end;
  const hasActuals = schedule.actualstart != null || schedule.actualend != null;
  functionToApply(
    deserializeYesplanAPIDateAndTime(startString),
    deserializeYesplanAPIDateAndTime(endString),
    hasActuals
  );
}

export default function getRostersForDay(
  day,
  schedules,
  dayline,
  dateFormatSetting,
  timeFormatSetting
) {
  // Create a new day rosters object that will keep track of all the rosters
  if (!dayline) return new DayRosters(dateFormatSetting, timeFormatSetting);

  let dayRosters;
  let rosters;
  withYesplanDayStartEndDo(day, dayline, (dayStart, dayEnd) => {
    dayRosters = new DayRosters(dateFormatSetting, timeFormatSetting, dayStart, dayEnd);
    // filter all the schedules to only include those for the day that we are processing
    rosters = schedules.filter(
      (schedule) =>
        deserializeYesplanAPIDateAndTime(schedule.start).isBefore(dayEnd) &&
        deserializeYesplanAPIDateAndTime(schedule.end).isAfter(dayStart)
    );
  });

  // Important: we sort the rosters based on their start and then categorize them based on their type
  const sortedRosters = rosters.sort(
    (s1, s2) =>
      deserializeYesplanAPIDateAndTime(s1.start) -
      deserializeYesplanAPIDateAndTime(s2.start)
  );
  const resourceBookings = sortedRosters.filter((roster) => isResourceBooking(roster));
  const unavailabilities = sortedRosters.filter((roster) => isUnavailability(roster));
  const breaks = sortedRosters.filter((roster) => isBreak(roster));
  const shifts = sortedRosters.filter((roster) => isShift(roster));

  // First we process the shifts, afterwards the breaks that will be splitting up the shifts
  shifts.forEach((shift) =>
    withStartEndAndHasActualsDo(shift, (start, end, hasActuals) =>
      dayRosters.addShift(new RosterShift(start, end, hasActuals))
    )
  );

  // After processing the shifts, also add the no rosters to fill up the gaps between the shifts
  dayRosters.addNoRosters();

  breaks.forEach((breakSchedule) =>
    withStartEndAndHasActualsDo(breakSchedule, (start, end, hasActuals) =>
      dayRosters.addBreak(new RosterBreak(start, end, hasActuals))
    )
  );

  // Also add the unavailabilities as top-level entities
  unavailabilities.forEach((unavailability) =>
    withStartEndAndHasActualsDo(unavailability, (start, end, hasActuals) =>
      dayRosters.addUnavailability(
        new RosterUnavailability(start, end, hasActuals, unavailability.reason)
      )
    )
  );

  // For each resourcebooking, we create a matching object and process that object
  // The bookings will be summarized inside of the DayRosters object
  resourceBookings.forEach((resourceBooking) =>
    withStartEndAndHasActualsDo(resourceBooking, (start, end, hasActuals) =>
      dayRosters.addBooking(
        new RosterBooking(
          start,
          end,
          hasActuals,
          start,
          end,
          resourceBooking.resourcebooking
        )
      )
    )
  );
  // When creating the schedules, overlapping bookings get split into different parts
  // and when overlapping items cross the dayline those parts can appear on the wrong day
  // This is why we have to filter those out after the roster has been created.
  dayRosters.filterOutBookingsOnOtherDay(day, dayline).sort();
  return dayRosters;
}
